<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted, inject, watch, provide } from 'vue';
    import { RouterView, useRoute, useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { FeatureService } from '@shared/directives/Feature.service';
    import type { MoreAction } from '@shared/components/List/ListTypes';
    import ContactList from '@views/Lists/ContactList.vue';
    import Loading from '@shared/components/Loading.vue';
    import ListNavbar from '@views/Lists/ListNavbar.vue';
    import ListHeader from '@views/Lists/ListHeader.vue';
    import ContactModal from '@views/Contact/ContactModal.vue';
    import PreUnsubscribeFromCategoryModal from '@views/Lists/components/PreUnsubscribeFromCategoryModal.vue';
    import { type List } from './ListsTypes.d';
    import { AccessControlList, AccessControlLevel, CurrentObject } from '@cwd/Acl/Acl.access';
    import { hasAccessLevel } from '@cwd/Acl/useAclAccess';
    import AclEditor from '@cwd/Acl/AclEditor.vue';
    import type { ContactsResponse } from './ContactList.access';
    import {
        getList,
        getListContacts,
        exportListContactsExcel,
        exportListContactsCsv,
        exportListContactsToNewList,
        exportListContactsToExistingList,
        exportListContactsByMail,
        addContactToList,
    } from './Lists.access';
    import { removeContactsFromList } from './ContactList.access';
    import { type ContactData } from '@views/Contact/ContactTypes';
    import { loadContact } from '@views/Contact/Contact.access';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';

    const props = defineProps<{ listId: string }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const hasContactsWriteScope = scopeAccess.hasScope({ Feature: 'Contacts', Permission: Permissions.Write } as vRequireScopeParams);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    // The backend now rejects any write to this list (rename, retag, add/remove contacts, welcome
    // issue/SMS) when the caller lacks Write on the list's own ACL -- this drives the UI so that
    // block is never a surprise (disabled controls instead of a 403 after the fact). Uses the
    // server-computed CurrentUserAccessLevel (group-aware) rather than hasAclWriteAccess's raw ACL
    // scan, which only matches a direct PrincipalId entry and fails open for group-only grants --
    // that gap let a Read-via-group user see an enabled Description field, save, and get bounced
    // to the Forbidden page by the (correctly group-aware) backend check.
    const canWrite = computed(() => hasAccessLevel(list.value?.CurrentUserAccessLevel, AccessControlLevel.Write));

    const PRE_UNSUBSCRIBE_FROM_CATEGORY_KEY = 'preUnsubscribeFromCategory';

    const moreActions = computed<MoreAction[]>(() => {
        if (!featureAccess.hasFeature('PreUnsubscribed')) return [];
        return [{ key: PRE_UNSUBSCRIBE_FROM_CATEGORY_KEY, display: $Resources.PreUnsubscribeFromCategoryOpenModalBtn }];
    });

    function onMoreActionClick(key: string) {
        if (key === PRE_UNSUBSCRIBE_FROM_CATEGORY_KEY) {
            showPreUnsubscribeModal.value = true;
        }
    }

    const IMPORT_POLL_DELAY_MS = 20000;
    const IMPORT_POLL_MAX_RETRIES = 30;

    const route = useRoute();
    const router = useRouter();
    const showingContact = computed(() => route.name === 'ContactInList');
    const showingImport = computed(() => route.name === 'ListImport');

    const lastViewedContactId = ref<string | null>(null);
    const contactWasUpdated = ref(false);

    provide('markContactUpdated', () => {
        contactWasUpdated.value = true;
    });

    watch(
        () => route.params.contactId as string | undefined,
        (id) => {
            if (id) {
                lastViewedContactId.value = id;
                contactWasUpdated.value = false;
            }
        }
    );

    watch(showingContact, async (showing, wasShowing) => {
        if (wasShowing && !showing && lastViewedContactId.value && contactWasUpdated.value) {
            const updated = await loadContact(lastViewedContactId.value);
            contactListRef.value?.updateContact(updated);
        }
    });

    const loading = ref(true);
    const list = ref<List | null>(null);
    const importStarted = ref(false);

    onMounted(async () => {
        try {
            list.value = await getList(props.listId);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
            router.push({ name: 'Lists' });
            return;
        }
        loading.value = false;

        if (route.query.mergedList) {
            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertListsMerged });
            router.replace({ query: { ...route.query, mergedList: undefined } });
        }

        if (list.value.ActiveImportsCount) {
            handleImportStarted();
        }
    });

    watch(
        () => route.query.importStarted,
        (val) => {
            if (val) {
                importStarted.value = true;
                handleImportStarted();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.query.importScheduled,
        (val) => {
            if (val) {
                if (list.value) {
                    list.value.ScheduledImportsCount = list.value.ScheduledImportsCount + 1;
                }
                bsAlert({ title: $Resources.ListImportStartedHeading, content: $Resources.ListImportStartedBody });
                router.replace({ query: { ...route.query, importScheduled: undefined } });
            }
        },
        { immediate: true }
    );

    let importPollIntervalId: ReturnType<typeof setInterval> | null = null;
    let importPollRetries = 0;

    function stopImportPoll() {
        if (importPollIntervalId !== null) {
            clearInterval(importPollIntervalId);
            importPollIntervalId = null;
        }
    }

    onUnmounted(stopImportPoll);

    function handleImportStarted() {
        if (!list.value || importPollIntervalId !== null) return;
        bsAlert({ title: $Resources.ListImportStartedHeading, content: $Resources.ListImportStartedBody });

        importPollRetries = 0;
        importPollIntervalId = setInterval(async () => {
            importPollRetries++;

            try {
                const updated = await getList(props.listId);
                list.value!.ActiveImportsCount = updated.ActiveImportsCount;

                if (!list.value!.ActiveImportsCount) {
                    stopImportPoll();
                    importStarted.value = false;
                    list.value = updated;
                    contactListRef.value?.refresh();
                    router.replace({ query: {} });
                    return;
                }
            } catch {
                // continue polling on transient error
            }

            if (importPollRetries >= IMPORT_POLL_MAX_RETRIES) {
                stopImportPoll();
            }
        }, IMPORT_POLL_DELAY_MS);
    }

    let fetchAbortController: AbortController | null = null;

    async function fetchContacts(skip: number, pageSize: number, extra: string): Promise<ContactsResponse> {
        fetchAbortController?.abort();
        fetchAbortController = new AbortController();
        return getListContacts(props.listId, skip, pageSize, extra, fetchAbortController.signal);
    }

    async function exportExcel(extra: string): Promise<{ Url: string }> {
        return exportListContactsExcel(props.listId, extra);
    }

    async function exportCsv(extra: string): Promise<{ Url: string }> {
        return exportListContactsCsv(props.listId, extra);
    }

    async function exportToNewList(extra: string): Promise<{ ListId: string }> {
        return exportListContactsToNewList(props.listId, extra);
    }

    async function exportToExistingList(extra: string, targetListId: string): Promise<{ ListId: string }> {
        return exportListContactsToExistingList(props.listId, extra, targetListId);
    }

    async function sendExcelByMail(extra: string): Promise<void> {
        return exportListContactsByMail(props.listId, extra);
    }

    const showAclModal = ref(false);
    const showPreUnsubscribeModal = ref(false);

    function onSaveAcl(acl: AccessControlList[]) {
        showAclModal.value = false;
        if (!list.value) return;
        list.value.AccessControlList = acl;
    }

    const contactListRef = ref<InstanceType<typeof ContactList> | null>(null);
    const newContact = ref<string | null>(null);

    const openNewContactModal = () => {
        newContact.value = 'new';
    };

    const closeNewContactModal = () => {
        newContact.value = null;
    };

    async function addToList(contactData: ContactData): Promise<void> {
        if (loading.value) return;
        loading.value = true;

        try {
            await addContactToList(props.listId, contactData.contact.ContactId);
            contactListRef.value?.pushContact(contactData.contact);
            newContact.value = null;
            router.push({ name: 'ContactInList', params: { listId: props.listId, contactId: contactData.contact.ContactId } });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.errorContactNotAddedToListHeading, content: $Resources.errorContactNotAddedToListBody });
        } finally {
            loading.value = false;
        }
    }

    const newVersionAlertContent = () => {
        const url = `/Lists/${props.listId}/Old`;
        return $Resources.GeneralNewVersionAlertContent.replace('{{', `<a href="${url}">`).replace('}}', '</a>');
    };
</script>

<template>
    <BsAlert
        :show="!showingContact"
        class="m-0-b"
        :type="AlertTypes.Info"
        :title="$Resources.GeneralNewVersionAlertTitle"
        :content="newVersionAlertContent()" />

    <RouterView v-if="showingContact" />
    <RouterView v-if="showingImport" />

    <div v-if="list" v-show="!showingContact && !showingImport">
        <ListNavbar
            :listId="props.listId"
            :hasContactsWriteScope="hasContactsWriteScope && canWrite"
            :aclCount="list?.AccessControlList?.length ?? 0"
            @newContact="openNewContactModal"
            @openAcl="showAclModal = true" />

        <ListHeader :listId="props.listId" :list="list" :importStarted="importStarted" :canWrite="canWrite" />

        <ContactList
            ref="contactListRef"
            :isCheckable="canWrite"
            :fetchContacts="fetchContacts"
            :deleteContactsFn="(ids) => removeContactsFromList(props.listId, ids)"
            :exportExcel="exportExcel"
            :exportCsv="exportCsv"
            :exportToNewList="exportToNewList"
            :exportToExistingList="exportToExistingList"
            :sendExcelByMail="sendExcelByMail"
            :contactRoute="(contactId) => ({ name: 'ContactInList', params: { listId: props.listId, contactId } })"
            :moreActions="moreActions"
            :odataV4="true"
            @moreActionClick="onMoreActionClick" />
    </div>

    <ContactModal :contactId="newContact" :updateIdAfterCreated="false" @created="(newContact) => addToList(newContact)" @closed="closeNewContactModal" />

    <AclEditor
        :selected-groups="
            list?.AccessControlList?.map(
                (acl) =>
                    ({
                        ...acl,
                        GroupId: acl.PrincipalId,
                        AccessControlList: acl,
                    }) as CurrentObject
            ) || []
        "
        type="Lists"
        :objectId="props.listId"
        :open="showAclModal"
        @save="onSaveAcl"
        @close="showAclModal = false" />

    <PreUnsubscribeFromCategoryModal
        v-if="list"
        :show="showPreUnsubscribeModal"
        :listId="props.listId"
        :listName="list.Name"
        @close="showPreUnsubscribeModal = false" />

    <Loading :show="loading" :fixed="true" loadingStyle="progress-bar" />
</template>
